/**
 * Collection / artist grid: URL-driven filters + Section Rendering API swaps for `#collection-results`.
 */
(function () {
  'use strict';

  var RESULTS_ID = (window.BB && window.BB.RESULTS_ID) || 'collection-results';
  var BUSY_OPACITY = '0.65';

  function getResultsRoot() {
    return document.getElementById(RESULTS_ID);
  }

  function getSectionIdFromForm(form) {
    return (
      form.getAttribute('data-section-id') ||
      (getResultsRoot() && getResultsRoot().getAttribute('data-section-id'))
    );
  }

  function normalizePriceFields(form) {
    var gte = form.querySelector('input[name="filter.v.price.gte"]');
    var lte = form.querySelector('input[name="filter.v.price.lte"]');
    if (!gte || !lte) return;
    var a = parseFloat(String(gte.value).trim());
    var b = parseFloat(String(lte.value).trim());
    if (!isNaN(a) && !isNaN(b) && a > b) {
      gte.value = String(b);
      lte.value = String(a);
    }
  }

  function buildParamsFromForm(form, options) {
    options = options || {};
    var dropPage = options.dropPage !== false;
    var fd = new FormData(form);
    var params = new URLSearchParams();
    fd.forEach(function (value, key) {
      if (value === '' || value === null || typeof value === 'undefined') return;
      if (dropPage && key === 'page') return;
      params.append(key, value);
    });
    return params;
  }

  function displayUrlFromForm(form, params) {
    var u = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
    u.search = params.toString();
    return u;
  }

  function fetchSectionHtml(displayUrl, sectionId) {
    var fetchUrl = new URL(displayUrl.toString());
    fetchUrl.searchParams.set('section_id', sectionId);
    return fetch(fetchUrl.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    }).then(function (res) {
      if (!res.ok) throw new Error('fetch failed');
      return res.text();
    });
  }

  function replaceResultsFromHtml(htmlText, displayUrl, options) {
    options = options || {};
    var doc = new DOMParser().parseFromString(htmlText, 'text/html');
    var fresh = doc.getElementById(RESULTS_ID);
    if (!fresh) throw new Error('missing collection results');
    var root = getResultsRoot();
    if (!root) throw new Error('missing results mount');
    root.innerHTML = fresh.innerHTML;
    syncToolbarFromUrl(root, displayUrl);
    if (!options.skipPushState && typeof history !== 'undefined' && history.pushState) {
      history.pushState({}, '', displayUrl.pathname + displayUrl.search + displayUrl.hash);
    }
    if (window.lucide && typeof window.lucide.createIcons === 'function') {
      window.lucide.createIcons({ root: root });
    }
    root.dispatchEvent(new CustomEvent('collection:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('collection:updated', { bubbles: true }));
  }

  function syncToolbarFromUrl(root, url) {
    var params = url.searchParams;
    var gte = params.get('filter.v.price.gte') || '';
    var lte = params.get('filter.v.price.lte') || '';
    var sel = root.querySelector('[data-collection-price-select]');
    if (sel) {
      var token = gte + '-' + lte;
      var match = '';
      var opts = sel.options;
      for (var i = 0; i < opts.length; i++) {
        if (opts[i].value === token) { match = token; break; }
      }
      sel.value = match;
    }
    var gteInput = root.querySelector('input[name="filter.v.price.gte"]');
    var lteInput = root.querySelector('input[name="filter.v.price.lte"]');
    if (gteInput) gteInput.value = gte;
    if (lteInput) lteInput.value = lte;

    // Sort + availability: custom-mode toolbars (e.g. artist pages) don't get
    // these re-rendered server-side, so restore them from the URL too.
    var sortSel = root.querySelector('select[name="sort_by"]');
    var sortVal = params.get('sort_by');
    if (sortSel && sortVal) sortSel.value = sortVal;

    var availInput = root.querySelector('input[name="filter.v.availability"]');
    if (availInput) availInput.checked = params.get('filter.v.availability') === '1';
  }

  function setBusy(busy) {
    var root = getResultsRoot();
    if (!root) return;
    root.setAttribute('aria-busy', busy ? 'true' : 'false');
    root.style.opacity = busy ? BUSY_OPACITY : '';
    root.style.transition = busy ? 'opacity 150ms ease' : '';
  }

  function announceApplying(form, busy) {
    var el = form && form.querySelector('[data-collection-toolbar-live]');
    if (!el) return;
    if (busy) {
      el.textContent = form.getAttribute('data-applying-label') || '';
    } else {
      el.textContent = '';
    }
  }

  function clearAnnounceAfterSwap() {
    var root = getResultsRoot();
    var form = root && root.querySelector('form[data-collection-filter-form]');
    announceApplying(form, false);
  }

  function isClientMode() {
    var root = getResultsRoot();
    return !!(root && root.getAttribute('data-collection-mode') === 'client');
  }

  function pushClientState(displayUrl) {
    var root = getResultsRoot();
    if (typeof history !== 'undefined' && history.pushState) {
      history.pushState({}, '', displayUrl.pathname + displayUrl.search + displayUrl.hash);
    }
    if (root) syncToolbarFromUrl(root, displayUrl);
    document.dispatchEvent(
      new CustomEvent('collection:apply-client', { detail: { url: displayUrl } })
    );
  }

  function applyFromForm(form, options) {
    options = options || {};
    normalizePriceFields(form);
    var sectionId = getSectionIdFromForm(form);
    if (!sectionId) {
      form.submit();
      return;
    }
    var params = buildParamsFromForm(form, { dropPage: options.dropPage !== false });
    var displayUrl = displayUrlFromForm(form, params);
    if (isClientMode()) {
      pushClientState(displayUrl);
      return;
    }
    setBusy(true);
    announceApplying(form, true);
    return fetchSectionHtml(displayUrl, sectionId)
      .then(function (html) {
        replaceResultsFromHtml(html, displayUrl);
      })
      .catch(function () {
        window.location.assign(displayUrl.toString());
      })
      .finally(function () {
        setBusy(false);
        clearAnnounceAfterSwap();
      });
  }

  function onPaginationClick(ev) {
    var a = ev.target.closest('.theme-pagination a');
    if (!a || !a.getAttribute('href')) return;
    var root = getResultsRoot();
    if (!root || !root.contains(a)) return;
    ev.preventDefault();
    var displayUrl = new URL(a.getAttribute('href'), window.location.origin);
    if (isClientMode()) {
      pushClientState(displayUrl);
      return;
    }
    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;
    setBusy(true);
    fetchSectionHtml(displayUrl, sectionId)
      .then(function (html) {
        replaceResultsFromHtml(html, displayUrl);
      })
      .catch(function () {
        window.location.assign(displayUrl.toString());
      })
      .finally(function () {
        setBusy(false);
      });
  }

  function onPopState() {
    var root = getResultsRoot();
    if (!root) return;
    var displayUrl = new URL(window.location.href);
    if (isClientMode()) {
      syncToolbarFromUrl(root, displayUrl);
      document.dispatchEvent(
        new CustomEvent('collection:apply-client', { detail: { url: displayUrl } })
      );
      return;
    }
    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;
    setBusy(true);
    fetchSectionHtml(displayUrl, sectionId)
      .then(function (html) {
        replaceResultsFromHtml(html, displayUrl, { skipPushState: true });
      })
      .catch(function () {
        window.location.reload();
      })
      .finally(function () {
        setBusy(false);
      });
  }

  function shouldReactToToolbarChange(el) {
    if (!el) return false;
    if (el.hasAttribute && el.hasAttribute('data-collection-price-select')) return true;
    if (!el.name) return false;
    if (el.name === 'sort_by' && el.tagName === 'SELECT') return true;
    if (el.name === 'filter.v.availability') return true;
    if (el.name === 'filter.v.price.gte' || el.name === 'filter.v.price.lte') return true;
    return false;
  }

  function applyPriceSelect(form, select) {
    var val = String(select.value || '');
    var parts = val.split('-');
    var gte = form.querySelector('input[name="filter.v.price.gte"]');
    var lte = form.querySelector('input[name="filter.v.price.lte"]');
    if (gte) gte.value = parts[0] || '';
    if (lte) lte.value = parts[1] || '';
  }

  function onDelegatedChange(ev) {
    var el = ev.target;
    if (!el || !el.closest) return;
    if (!shouldReactToToolbarChange(el)) return;
    var form = el.closest('form[data-collection-filter-form]');
    if (!form) return;
    if (el.hasAttribute && el.hasAttribute('data-collection-price-select')) {
      applyPriceSelect(form, el);
    }
    applyFromForm(form, { dropPage: true });
  }

  function onDelegatedSubmit(ev) {
    var form = ev.target;
    if (!form.matches || !form.matches('form[data-collection-filter-form]')) return;
    ev.preventDefault();
    applyFromForm(form, { dropPage: true });
  }

  function onDelegatedPresetClick(ev) {
    var btn = ev.target.closest('[data-collection-price-preset]');
    if (!btn) return;
    var form = btn.closest('form[data-collection-filter-form]');
    if (!form) return;
    ev.preventDefault();
    var gte = form.querySelector('input[name="filter.v.price.gte"]');
    var lte = form.querySelector('input[name="filter.v.price.lte"]');
    if (gte) gte.value = btn.getAttribute('data-price-gte') || '';
    if (lte) lte.value = btn.getAttribute('data-price-lte') || '';
    applyFromForm(form, { dropPage: true });
  }

  function init() {
    document.addEventListener('change', onDelegatedChange);
    document.addEventListener('submit', onDelegatedSubmit);
    document.addEventListener('click', onDelegatedPresetClick);
    var root = getResultsRoot();
    if (root) {
      root.addEventListener('click', onPaginationClick);
    }
    window.addEventListener('popstate', onPopState);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
